import {
  DATABASE_ROW_SHARD_ORDER_ARRAY_KEY,
  DATABASE_ROW_SHARD_ROWS_MAP_KEY,
  type DatabaseShardManifest,
  type DatabaseShardManifestEntry,
  normalizeDatabaseShardManifest,
} from '@blocksuite/affine-model';
import {
  type DataSourceRowsWindow,
  type DataSourceWindowRequest,
} from '@blocksuite/data-view';
import { BlockSuiteError, ErrorCode } from '@blocksuite/global/exceptions';
import { resolveDatabaseCellStorageKey } from '@streamlain/database-identity';
import { encodeStateAsUpdate, encodeStateVector } from 'yjs';

import {
  type DatabaseRowShardDocLoadContext,
  type DatabaseRowShardDocLoadReason,
  type RawValueJsonConverter,
  type RowShardCells,
  type ShardedRowRecord,
} from './data-source-contracts.js';
import type {
  DatabaseShardDocument,
  DatabaseSourceRuntime,
} from './data-source-runtime-context.js';
import {
  disposeUnknown,
  fromJsonValue,
  isReadonlyYMap,
  normalizeDatabaseRowWindowRequest,
  normalizeRowShardCell,
  projectionSearchCellText,
  rowShardDocLoaders,
  uint8ArraysEqual,
} from './data-source-values.js';

export function getShardDocIds(
  this: DatabaseSourceRuntime,
  shard: DatabaseShardManifestEntry
): string[] {
  if (!shard.serverDocId) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      `Database row shard "${shard.shardId}" is missing its server document id.`
    );
  }
  return [shard.serverDocId];
}

export function getExistingShardDocs(
  this: DatabaseSourceRuntime,
  shard: DatabaseShardManifestEntry
): DatabaseShardDocument[] {
  const workspace = this._model.store.workspace;
  return this.getShardDocIds(shard)
    .map(docId => workspace.getDoc(docId))
    .filter(doc => !!doc);
}

export function hasLoadedShardRows(
  this: DatabaseSourceRuntime,
  doc: DatabaseShardDocument | null | undefined
): boolean {
  return (
    !!doc &&
    doc.spaceDoc.getArray(DATABASE_ROW_SHARD_ORDER_ARRAY_KEY).length > 0 &&
    doc.spaceDoc.getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY).size > 0
  );
}

export function hasCompleteLoadedShardRows(
  this: DatabaseSourceRuntime,
  shard: DatabaseShardManifestEntry,
  doc: DatabaseShardDocument | null | undefined
): boolean {
  if (!doc) return false;
  const rowOrder = doc.spaceDoc.getArray<string>(
    DATABASE_ROW_SHARD_ORDER_ARRAY_KEY
  );
  const rowsMap = doc.spaceDoc.getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY);
  if (this.isFileIndexDatabase()) {
    const rowIds = rowOrder.toArray().map(id => String(id));
    return (
      new Set(rowIds).size === rowIds.length &&
      rowIds.every(rowId => rowsMap.has(rowId))
    );
  }
  if (shard.rowCount === 0) {
    return rowOrder.length === 0 && rowsMap.size === 0;
  }
  const rows = this.readLoadedShardRows(shard);
  return (
    rowOrder.length === shard.rowCount &&
    rowsMap.size >= shard.rowCount &&
    rows.length === shard.rowCount
  );
}

export function getExistingShardDoc(
  this: DatabaseSourceRuntime,
  shard: DatabaseShardManifestEntry
): DatabaseShardDocument | null {
  const docs = this.getExistingShardDocs(shard);
  return docs.find(doc => this.hasLoadedShardRows(doc)) ?? docs[0] ?? null;
}

export function ensureWorkspaceDoc(
  this: DatabaseSourceRuntime,
  docId: string
): DatabaseShardDocument | undefined {
  const workspace = this._model.store.workspace;
  let doc: DatabaseShardDocument | undefined =
    workspace.getDoc(docId) ?? undefined;
  if (!doc) {
    const registerDoc = (workspace as { registerDoc?: (id: string) => void })
      .registerDoc;
    if (registerDoc) {
      registerDoc.call(workspace, docId);
      doc = workspace.getDoc(docId) ?? undefined;
    } else {
      doc = workspace.createDoc(docId);
    }
  }
  return doc;
}

export function requestRowShardDocLoad(
  this: DatabaseSourceRuntime,
  docId: string,
  reason: DatabaseRowShardDocLoadReason
): void {
  if (this._rowShardDocLoadDisposers.has(docId)) {
    return;
  }

  // Loaders belong to the host page lifecycle. For sharded databases the
  // storage model lives in a different root document, so looking the loader
  // up on `_model.store` silently misses the registered host-page loader.
  const loaders = rowShardDocLoaders.get(this._hostModel.store);
  if (!loaders || loaders.size === 0) {
    return;
  }

  const disposers: Array<() => void> = [];
  const context: DatabaseRowShardDocLoadContext = {
    blockId: this.blockId,
    databaseId: this.databaseId,
    reason,
    workspaceId: this._model.store.workspace.id,
  };
  for (const loader of loaders) {
    const dispose = loader(docId, context);
    if (typeof dispose === 'function') {
      disposers.push(dispose);
    }
  }
  this._rowShardDocLoadDisposers.set(docId, disposers);
}

export function observeShardDocReady(
  this: DatabaseSourceRuntime,
  doc: DatabaseShardDocument
): void {
  if (!this._shardedReadyObservedDocs.has(doc.id)) {
    this._shardedReadyObservedDocs.add(doc.id);
    const readySlot = (
      doc as {
        slots?: { ready?: { subscribe?: (cb: () => void) => unknown } };
      }
    ).slots?.ready;
    const disposable = readySlot?.subscribe?.(() =>
      this.queueShardedRowsRefresh()
    );
    if (disposable) {
      this._shardedReadyDisposers.set(doc.id, () => disposeUnknown(disposable));
    }
    if (!doc.ready) {
      doc.load();
      this.queueShardedRowsRefresh();
    }
  }
}

export function ensureShardDocsLoaded(
  this: DatabaseSourceRuntime,
  shard: DatabaseShardManifestEntry,
  reason: DatabaseRowShardDocLoadReason
): DatabaseShardDocument[] {
  const docs = this.getShardDocIds(shard)
    .map(docId => {
      this.requestRowShardDocLoad(docId, reason);
      return this.ensureWorkspaceDoc(docId);
    })
    .filter(doc => !!doc);

  if (docs.length === 0) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      `Cannot load database row shard document "${shard.serverDocId}"`
    );
  }

  docs.forEach(doc => this.observeShardDocReady(doc));
  return docs;
}

export function ensureShardObserved(
  this: DatabaseSourceRuntime,
  shard: DatabaseShardManifestEntry,
  reason: DatabaseRowShardDocLoadReason = 'row-window'
): DatabaseShardDocument {
  const docs = this.ensureShardDocsLoaded(shard, reason);
  let observerAdded = false;
  for (const shardDoc of docs) {
    const observedDocId = shardDoc.id;
    if (this._shardedObservedDocs.has(observedDocId)) {
      continue;
    }
    this._shardedObservedDocs.add(observedDocId);
    observerAdded = true;
    const rowsMap = shardDoc.spaceDoc.getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY);
    const rowOrder = shardDoc.spaceDoc.getArray(
      DATABASE_ROW_SHARD_ORDER_ARRAY_KEY
    );
    const refreshRows = () => this.queueShardedRowsRefresh();
    const refreshRowOrder = () => this.queueShardedRowsRefresh();
    const refreshDocUpdate = () => this.queueShardedRowsRefresh();
    rowsMap.observeDeep(refreshRows);
    rowOrder.observeDeep(refreshRowOrder);
    shardDoc.spaceDoc.on('update', refreshDocUpdate);
    this._shardedDocObserverDisposers.set(observedDocId, () => {
      rowsMap.unobserveDeep(refreshRows);
      rowOrder.unobserveDeep(refreshRowOrder);
      shardDoc.spaceDoc.off('update', refreshDocUpdate);
    });
  }
  if (observerAdded) {
    this.queueShardedRowsRefresh();
  }
  const shardDoc = this.getExistingShardDoc(shard) ?? docs[0];
  if (!shardDoc) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      `Cannot load database row shard document "${shard.serverDocId}"`
    );
  }
  return shardDoc;
}

export function ensureShardsForRowsWindow(
  this: DatabaseSourceRuntime,
  manifest: ReturnType<typeof normalizeDatabaseShardManifest>,
  offset: number,
  limit: number
): void {
  if (!manifest) return;
  const activeDocIds = new Set<string>();
  for (const shard of this.getManifestShardsForRowsWindow(
    manifest,
    offset,
    limit
  )) {
    this.ensureShardObserved(shard);
    for (const docId of this.getShardDocIds(shard)) {
      activeDocIds.add(docId);
    }
  }
  this._activeShardedWindowDocs = activeDocIds;
}

export function readLoadedShardRows(
  this: DatabaseSourceRuntime,
  shard: DatabaseShardManifestEntry
): ShardedRowRecord[] {
  const shardDoc = this.getExistingShardDoc(shard);
  if (!shardDoc) return [];
  const stateVector = encodeStateVector(shardDoc.spaceDoc);
  const cached = this._shardedRowsByDocId.get(shardDoc.id);
  if (cached && uint8ArraysEqual(cached.stateVector, stateVector)) {
    return cached.rows;
  }
  const rowsMap = shardDoc.spaceDoc.getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY);
  const rowOrder = shardDoc.spaceDoc.getArray<string>(
    DATABASE_ROW_SHARD_ORDER_ARRAY_KEY
  );
  const rowIds = rowOrder.toArray().map(rowId => String(rowId));
  if (rowsMap.size > 0 && rowIds.length === 0) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      `Database row shard "${shardDoc.id}" is missing ${DATABASE_ROW_SHARD_ORDER_ARRAY_KEY}.`
    );
  }
  const uniqueRowIds: string[] = [];
  const seenRowIds = new Set<string>();
  rowIds.forEach(rowId => {
    if (seenRowIds.has(rowId)) {
      return;
    }
    seenRowIds.add(rowId);
    uniqueRowIds.push(rowId);
  });
  const rows: ShardedRowRecord[] = [];
  for (const rowId of uniqueRowIds) {
    const rawCells = rowsMap.get(rowId);
    if (!isReadonlyYMap(rawCells)) continue;
    const cells: RowShardCells = {};
    rawCells.forEach((rawCell, columnId) => {
      cells[columnId] = normalizeRowShardCell(columnId, rawCell);
    });
    rows.push({ id: rowId, cells });
  }
  this._shardedRowsByDocId.set(shardDoc.id, {
    rows,
    stateVector: encodeStateVector(shardDoc.spaceDoc),
  });
  return rows;
}

export function loadShardedRowFromExistingDocs(
  this: DatabaseSourceRuntime,
  rowId: string
): void {
  const manifest = normalizeDatabaseShardManifest(
    this._model.props.databaseShardManifest
  );
  if (!manifest) return;

  for (const shard of manifest.shards) {
    const shardDoc = this.getExistingShardDoc(shard);
    if (!shardDoc) continue;
    const rowOrder = shardDoc.spaceDoc.getArray<string>(
      DATABASE_ROW_SHARD_ORDER_ARRAY_KEY
    );
    if (!rowOrder.toArray().some(id => String(id) === rowId)) {
      continue;
    }

    let cacheChanged = false;
    for (const row of this.readLoadedShardRows(shard)) {
      this._shardedRowsById.set(row.id, row);
      cacheChanged = true;
    }
    if (cacheChanged) {
      this._shardedRowsRevision.value += 1;
    }
    return;
  }
}

export function shardedRowsEqual(
  this: DatabaseSourceRuntime,
  rowIds: string[],
  rowsById: Map<string, ShardedRowRecord>
): boolean {
  if (rowIds.length !== this._shardedRowIds.length) {
    return false;
  }
  for (let index = 0; index < rowIds.length; index++) {
    if (rowIds[index] !== this._shardedRowIds[index]) {
      return false;
    }
  }
  if (rowsById.size !== this._shardedRowsById.size) {
    return false;
  }
  for (const [rowId, row] of rowsById) {
    const previous = this._shardedRowsById.get(rowId);
    if (!previous) {
      return false;
    }
    const cellIds = Object.keys(row.cells);
    const previousCellIds = Object.keys(previous.cells);
    if (cellIds.length !== previousCellIds.length) {
      return false;
    }
    for (const cellId of cellIds) {
      const cell = row.cells[cellId];
      const previousCell = previous.cells[cellId];
      if (!cell || !previousCell) {
        return false;
      }
      if (cell.columnId !== previousCell.columnId) {
        return false;
      }
      if (!this.serializedShardedCellEquals(cell.value, previousCell.value)) {
        return false;
      }
    }
  }
  return true;
}

export function getShardedRowsWindow(
  this: DatabaseSourceRuntime,
  request: DataSourceWindowRequest
): DataSourceRowsWindow {
  this._shardedRowsRevision.value;
  const { offset, limit } = normalizeDatabaseRowWindowRequest(request);
  const manifest = normalizeDatabaseShardManifest(
    this._model.props.databaseShardManifest
  );
  if (!manifest) return this.getUnshardedRowsWindow(request);

  this.ensureShardsForRowsWindow(manifest, offset, limit);

  const rowIds: string[] = [];
  const endOffset = offset + limit;
  let shardStart = 0;
  for (const shard of manifest.shards) {
    const shardEnd = shardStart + shard.rowCount;
    if (shardEnd > offset && shardStart < endOffset) {
      const shardRows = this.readLoadedShardRows(shard);
      for (const row of shardRows) {
        this._shardedRowsById.set(row.id, row);
      }
      const shardRowIds = shardRows.map(row => row.id);
      const localStart = Math.max(0, offset - shardStart);
      const localEnd = Math.min(shard.rowCount, endOffset - shardStart);
      rowIds.push(...shardRowIds.slice(localStart, localEnd));
    }
    if (shardStart >= endOffset) break;
    shardStart = shardEnd;
  }

  const totalCount = this.getManifestRowCount(manifest);
  return {
    offset,
    limit,
    totalCount,
    hasMore: offset + limit < totalCount,
    rowIds,
  };
}

export function queueShardedRowsRefresh(this: DatabaseSourceRuntime): void {
  if (this._disposed) return;
  if (this._shardedRefreshFrame !== null) return;
  this._shardedRefreshFrame = requestAnimationFrame(() => {
    this._shardedRefreshFrame = null;
    this.refreshObservedShardedRowsFromDocs();
  });
}

export function getObservedManifestShards(
  this: DatabaseSourceRuntime,
  manifest: DatabaseShardManifest
): DatabaseShardManifestEntry[] {
  return manifest.shards.filter(shard =>
    this.getShardDocIds(shard).some(docId =>
      this._shardedObservedDocs.has(docId)
    )
  );
}

export function getActiveWindowManifestShards(
  this: DatabaseSourceRuntime,
  manifest: DatabaseShardManifest
): DatabaseShardManifestEntry[] {
  return manifest.shards.filter(shard =>
    this.getShardDocIds(shard).some(docId =>
      this._activeShardedWindowDocs.has(docId)
    )
  );
}

export function refreshObservedShardedRowsFromDocs(
  this: DatabaseSourceRuntime
): void {
  const manifest = normalizeDatabaseShardManifest(
    this._model.props.databaseShardManifest
  );
  if (!manifest) return;
  const reconciledShards =
    this.reconcileFileIndexObservedShardManifest(manifest);
  const activeWindowShards = this.getActiveWindowManifestShards(manifest);
  this.refreshShardedRowsFromDocs(
    activeWindowShards.length > 0 ? activeWindowShards : reconciledShards
  );
}

export function reconcileFileIndexObservedShardManifest(
  this: DatabaseSourceRuntime,
  manifest: DatabaseShardManifest
): DatabaseShardManifestEntry[] {
  const observed = this.getObservedManifestShards(manifest);
  if (!this.isFileIndexDatabase()) return observed;
  const updates: DatabaseShardManifestEntry[] = [];
  const shards = manifest.shards.map(shard => {
    if (!observed.some(candidate => candidate.shardId === shard.shardId)) {
      return shard;
    }
    const shardDoc = this.getExistingShardDoc(shard);
    if (!shardDoc?.ready) return shard;
    const rowIds = shardDoc.spaceDoc
      .getArray<string>(DATABASE_ROW_SHARD_ORDER_ARRAY_KEY)
      .toArray()
      .map(id => String(id));
    const next = this.updateLocalShardManifestEntry(
      shard,
      rowIds,
      encodeStateAsUpdate(shardDoc.spaceDoc).byteLength
    );
    if (JSON.stringify(next) !== JSON.stringify(shard)) updates.push(next);
    return next;
  });
  if (updates.length > 0) this.publishLocalShardManifest(updates);
  return shards.filter(shard =>
    this.getShardDocIds(shard).some(docId =>
      this._shardedObservedDocs.has(docId)
    )
  );
}

export function refreshShardedRowsFromDocs(
  this: DatabaseSourceRuntime,
  shards?: readonly DatabaseShardManifestEntry[]
): void {
  if (!shards) {
    const manifest = normalizeDatabaseShardManifest(
      this._model.props.databaseShardManifest
    );
    if (!manifest) return;
    shards = this.getObservedManifestShards(manifest);
  }

  const manifest = normalizeDatabaseShardManifest(
    this._model.props.databaseShardManifest
  );
  const integrityIssue = manifest
    ? this.getAuthoritativeRowShardIntegrityIssue(manifest)
    : null;
  if (integrityIssue) {
    this._rowShardIntegrityError.value = integrityIssue.message;
    return;
  }
  const pendingIntegrityIssue = manifest
    ? this.getLoadedRowShardIntegrityIssue(manifest)
    : null;
  if (pendingIntegrityIssue) {
    this._rowShardIntegrityError.value = null;
    this.scheduleLoadedRowShardIntegrityValidation(
      manifest!,
      pendingIntegrityIssue
    );
    return;
  }
  if (this._rowShardIntegrityError.value !== null) {
    this._rowShardIntegrityError.value = null;
  }

  // Refresh only the requested window, then project every already
  // materialized shard cache in manifest order. Replacing the shared cache
  // with each caller's subset makes initial-window and full-projection
  // consumers oscillate forever (for example 150 -> 257 -> 150 rows).
  for (const shard of shards) {
    this.readLoadedShardRows(shard);
  }

  const rowIds: string[] = [];
  const rowsById = new Map<string, ShardedRowRecord>();
  for (const shard of manifest?.shards ?? shards) {
    const shardDoc = this.getExistingShardDoc(shard);
    if (!shardDoc) continue;
    const cached = this._shardedRowsByDocId.get(shardDoc.id);
    if (!cached) continue;
    for (const row of cached.rows) {
      const rowId = row.id;
      rowIds.push(rowId);
      rowsById.set(rowId, row);
    }
  }

  if (this.shardedRowsEqual(rowIds, rowsById)) {
    return;
  }

  this._shardedRowIds = rowIds;
  this._shardedRowsById = rowsById;
  this._shardedSearchTextByRowId.clear();
  this._shardedRowsRevision.value += 1;
}

export function getShardedProjectionCellValue(
  this: DatabaseSourceRuntime,
  propertyId: string,
  serialized: unknown
): unknown {
  const propertyType = this.isSpacialProperty(propertyId)
    ? propertyId
    : this.propertyTypeGet(propertyId);
  if (!propertyType) return serialized;
  const meta = this.propertyMetaGet(propertyType);
  if (!meta) return serialized;
  const data = this.propertyDataGet(
    this.isSpacialProperty(propertyId) ? propertyType : propertyId
  );
  const normalized =
    fromJsonValue(meta as RawValueJsonConverter, {
      data,
      dataSource: this.dataSource,
      value: serialized,
    }) ?? serialized;
  return (
    meta.config.rawValue.toJson?.({
      value: normalized,
      data,
      dataSource: this.dataSource,
    }) ?? normalized
  );
}

export function getShardedRowSearchText(
  this: DatabaseSourceRuntime,
  rowId: string,
  propertyIds: readonly string[]
): string {
  const propertySignature = propertyIds.join('\u0000');
  if (propertySignature !== this._shardedSearchPropertySignature) {
    this._shardedSearchPropertySignature = propertySignature;
    this._shardedSearchTextByRowId.clear();
  }
  const cached = this._shardedSearchTextByRowId.get(rowId);
  if (cached !== undefined) return cached;

  const row = this._shardedRowsById.get(rowId);
  const text = propertyIds
    .map(propertyId =>
      projectionSearchCellText(
        this.getShardedProjectionCellValue(
          propertyId,
          row?.cells[propertyId]?.value
        )
      )
    )
    .join('\u0000')
    .toLowerCase();
  this._shardedSearchTextByRowId.set(rowId, text);
  return text;
}

export function getShardedCellValue(
  this: DatabaseSourceRuntime,
  rowId: string,
  propertyId: string
): { found: boolean; value: unknown } {
  this._shardedRowsRevision.value;
  const row = this._shardedRowsById.get(rowId);
  if (!row) return { found: false, value: undefined };
  const storageKey = resolveDatabaseCellStorageKey(
    row.cells,
    propertyId,
    this._model.props.columns
  );
  const cell = row.cells[storageKey];
  if (!cell) return { found: false, value: undefined };
  return {
    found: true,
    value: this.normalizeShardedCellValueForProperty(
      rowId,
      propertyId,
      cell.value
    ),
  };
}
